import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import HabboImage from '../../../components/HabboImage';
import { getActiveSessions, killSession, killAllUserSessions, cleanExpiredSessions } from '../../../services/api';
import { toast } from 'react-toastify';

function SessionsAdmin({ userData }) {
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchSessions = async () => {
        try {
            const response = await getActiveSessions();
            setSessions(response.sessions);
        } catch (error) {
            console.error('Failed to fetch sessions:', error);
            toast.error('Failed to load sessions');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    const handleKillSession = async (tokenId) => {
        if (!window.confirm('Are you sure you want to terminate this session?')) {
            return;
        }

        try {
            await killSession(tokenId);
            toast.success('Session terminated');
            fetchSessions();
        } catch (error) {
            console.error('Failed to kill session:', error);
            toast.error('Failed to terminate session');
        }
    };

    const handleKillAllUserSessions = async (discordId, habboName) => {
        if (!window.confirm(`Terminate all sessions for ${habboName}?`)) {
            return;
        }

        try {
            const response = await killAllUserSessions(discordId);
            toast.success(response.message);
            fetchSessions();
        } catch (error) {
            console.error('Failed to kill user sessions:', error);
            toast.error('Failed to terminate sessions');
        }
    };

    const handleCleanExpired = async () => {
        try {
            await cleanExpiredSessions();
            toast.success('Expired sessions cleaned');
            fetchSessions();
        } catch (error) {
            console.error('Failed to clean sessions:', error);
            toast.error('Failed to clean expired sessions');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="red" header="Manage Current Sessions">
                <div className="mb-4 flex justify-between items-center">
                    <p className="font-['Jersey_10'] text-[14px] uppercase">Active Sessions: {sessions.length}</p>
                    <button
                        onClick={handleCleanExpired}
                        className="bg-blue-600 text-white px-4 py-2 font-['Jersey_10'] uppercase text-[14px] tracking-[1px]"
                        style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                    >
                        Clean Expired
                    </button>
                </div>

                {isLoading ? (
                    <p className="font-['Jersey_10'] text-[14px] uppercase">Loading sessions...</p>
                ) : sessions.length === 0 ? (
                    <p className="font-['Jersey_10'] text-[14px] uppercase">No active sessions</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                            <tr className="bg-maroon text-white">
                                <th className="border border-white px-4 py-2 text-left uppercase text-[14px] tracking-[1px] font-['Jersey_10']">
                                    Habbo Name
                                </th>
                                <th className="border border-white px-4 py-2 text-left uppercase text-[14px] tracking-[1px] font-['Jersey_10']">
                                    Discord ID
                                </th>
                                <th className="border border-white px-4 py-2 text-left uppercase text-[14px] tracking-[1px] font-['Jersey_10']">
                                    Created
                                </th>
                                <th className="border border-white px-4 py-2 text-left uppercase text-[14px] tracking-[1px] font-['Jersey_10']">
                                    Expires
                                </th>
                                <th className="border border-white px-4 py-2 text-center uppercase text-[14px] tracking-[1px] font-['Jersey_10']">
                                    Kill
                                </th>
                                <th className="border border-white px-4 py-2 text-center uppercase text-[14px] tracking-[1px] font-['Jersey_10']">
                                    Kill All
                                </th>
                                <th className="border border-white px-4 py-2 text-center uppercase text-[14px] tracking-[1px] font-['Jersey_10']">
                                    Avatar
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {sessions.map((session, index) => (
                                <tr
                                    key={session.TokenID}
                                    className={index % 2 === 0 ? 'bg-gray-100' : 'bg-gray-200'}
                                >
                                    <td className="border border-white px-4 py-2 font-['Jersey_10'] text-[13px] uppercase">
                                        {session.HabboName || 'Unknown'}
                                    </td>
                                    <td className="border border-white px-4 py-2 font-['Jersey_10'] text-[13px]">
                                        {session.DiscordID}
                                    </td>
                                    <td className="border border-white px-4 py-2 font-['Jersey_10'] text-[12px]">
                                        {formatDate(session.CreatedAt)}
                                    </td>
                                    <td className="border border-white px-4 py-2 font-['Jersey_10'] text-[12px]">
                                        {formatDate(session.ExpiresAt)}
                                    </td>
                                    <td className="border border-white px-4 py-2 text-center">
                                        <button
                                            onClick={() => handleKillSession(session.TokenID)}
                                            className="bg-red-600 text-white px-3 py-1 text-[14px] uppercase tracking-[1px] font-['Jersey_10']"
                                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' }}
                                        >
                                            Kill
                                        </button>
                                    </td>
                                    <td className="border border-white px-4 py-2 text-center">
                                        <button
                                            onClick={() => handleKillAllUserSessions(session.DiscordID, session.HabboName)}
                                            className="bg-orange-600 text-white px-3 py-1 text-[14px] uppercase tracking-[1px] font-['Jersey_10']"
                                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' }}
                                        >
                                            Kill All
                                        </button>
                                    </td>
                                    <td className="border border-white px-4 py-2 text-center">
                                        {session.HabboName ? (
                                            <HabboImage
                                                habbo={session.HabboName}
                                                size="a"
                                                alt={session.HabboName}
                                                className="inline-block"
                                            />
                                        ) : (
                                            <span className="text-gray-500 font-['Jersey_10'] text-[12px] uppercase">N/A</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </ContentBox>
        </AdminLayout>
    );
}

export default SessionsAdmin;